import React, { FC, useEffect, useState } from 'react';
import { TAppointment } from '@api/appointments/types';
import { ScheduleCalendar } from '@pages/Schedule/components/Calendar';
import { format, isSameDay } from 'date-fns';
import { SelectField } from '@components/Form/SelectField';
import { convertTimeToOptions } from '@helpers/convertTimeToOptions';
import { timeFormatter } from '@helpers/timeFormatter';
import { getFreeOfDay, getOverlappingTimeRanges } from '@helpers/getFreeOfDay';
import {
  TSchedule,
  TWeekday,
  TWorkingDays,
} from '@pages/Physicians/components/PhysiciansTable/types';
import { IBusySlot } from '@api/trial/types';
import { useLanguageContext } from '@hooks/useLanguageContext';
import { Info } from '@components/Info';
import { ColorDescription } from '@components/ColorDescription';
import { FormikValues, useFormikContext } from 'formik';
import { checkTimeInRange } from '@helpers/checkTimeInRange';

interface IEditAppointmentProps {
  selectedAppointment: TAppointment;
}

export const EditAppointment: FC<IEditAppointmentProps> = ({ selectedAppointment }) => {
  const { dictionary } = useLanguageContext();

  const firstAppointmentDate = selectedAppointment.patients.appointments
    .filter(
      (appointments: { trialId: string }) => appointments.trialId === selectedAppointment.trialId,
    )
    .sort(
      (a: { date: string | number | Date }, b: { date: string | number | Date }) =>
        new Date(a.date).getTime() - new Date(b.date).getTime(),
    )[0].date;

  const [dateOfVisit, setDateOfVisit] = useState<Date>(
    new Date(format(new Date(selectedAppointment.date), 'MM/dd/yyyy')),
  );

  const { setFieldValue, values } = useFormikContext<FormikValues>();

  const schedule =
    'days' in (selectedAppointment.trials.physicians.schedule || {})
      ? (selectedAppointment.trials.physicians.schedule as TSchedule).days
      : (selectedAppointment.trials.physicians.schedule as TWorkingDays);

  const onSelectDate = async (date: Date) => {
    setFieldValue('date', format(dateOfVisit, 'MM/dd/yyyy'));
    setDateOfVisit(date);
  };

  const unavailableDays = () => {
    const physicianDays = selectedAppointment?.trials?.physicians?.schedule?.days || {};

    const locationDays = (selectedAppointment?.locations?.schedule?.days as TWorkingDays) || {};

    const isMd = selectedAppointment.md;

    if (!physicianDays || !locationDays) {
      return [];
    }

    const unavailablePhysicianDays = Object.keys(physicianDays).filter(
      (day) => !physicianDays[day as keyof TWorkingDays]?.length,
    );

    const unavailableLocationDays = Object.keys(locationDays).filter(
      (day) => !locationDays[day as keyof TWorkingDays]?.length,
    );

    const allUnavailable = isMd
      ? [...new Set([...unavailablePhysicianDays, ...unavailableLocationDays])]
      : unavailableLocationDays;

    return allUnavailable;
  };

  const getDatesInRange = (date: string, duration: number) => {
    const currentDay = new Date(date)
      .toLocaleDateString('en-us', {
        weekday: 'long',
      })
      .toLowerCase();

    const physicianSchedule = selectedAppointment?.trials?.physicians?.schedule?.days;

    const locationSchedule = selectedAppointment?.locations?.schedule?.days;

    const isMd = selectedAppointment.md;

    if (!locationSchedule || !physicianSchedule) {
      return [];
    }

    const availableTimeRanges = isMd
      ? getOverlappingTimeRanges(locationSchedule, physicianSchedule, currentDay)
      : locationSchedule[currentDay as TWeekday];

    const busySlots = selectedAppointment.trials.busySlots
      .map(({ from, to }: { from: string; to: string }) => {
        if (isSameDay(new Date(from), new Date(date)) && isSameDay(new Date(to), new Date(date))) {
          return {
            from: timeFormatter(new Date(from)),
            to: timeFormatter(new Date(to)),
          };
        }
        return undefined;
      })
      .filter((slot: any) => slot !== undefined);

    return (
      schedule &&
      busySlots &&
      getFreeOfDay(duration || 15, availableTimeRanges, busySlots as IBusySlot[])
    );
  };

  useEffect(() => {
    setFieldValue('date', format(dateOfVisit, 'MM/dd/yyyy'));
  }, [setFieldValue, dateOfVisit]);

  const currentSlot =
    selectedAppointment.date === values.date ? selectedAppointment.time : undefined;

  const timeRange = () =>
    convertTimeToOptions(
      getDatesInRange(
        values.date || selectedAppointment.date,
        Number(selectedAppointment.duration),
      ),
      currentSlot,
    );

  const isEditing = () => !checkTimeInRange(timeRange(), values.time);

  const timeValue = () => {
    if (checkTimeInRange(timeRange(), values.time)) {
      return values.time;
    }
    if (checkTimeInRange(timeRange(), selectedAppointment.time)) {
      return selectedAppointment.time;
    }
    return '';
  };

  return (
    <>
      <section className="form-section">
        <Info
          text={dictionary.appointments.modals.editAppointment.info}
          phone={dictionary.appointments.modals.editAppointment.phone}
        />
      </section>
      <section className="form-section">
        <ColorDescription />
      </section>
      <section className="form-section">
        <div className="row">
          <div className="col-xs-12">
            <ScheduleCalendar
              name="date"
              controls={false}
              unavailableDays={unavailableDays()}
              visitDate={dateOfVisit}
              excludeDates={
                firstAppointmentDate === selectedAppointment.date ? undefined : firstAppointmentDate
              }
              recepitDate={new Date(selectedAppointment.originalDate)}
              protocolWindow={Number(
                Object.values(selectedAppointment.trials.appointmentTemplate).find(
                  (trial) =>
                    trial?.name === selectedAppointment.name ||
                    trial?.notifications_settings === selectedAppointment.notificationsSettings ||
                    trial?.duration === selectedAppointment.duration ||
                    trial?.type === selectedAppointment.type,
                )?.protocolWindow,
              )}
              handleChange={onSelectDate}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12">
            <SelectField
              autoField={values.time !== ''}
              selectOptions={timeRange()}
              isEditing={isEditing()}
              value={timeValue()}
              placeholder={dictionary.placeholders.chooseTime}
              label={dictionary.appointments.table.time}
              required
              name="time"
            />
          </div>
        </div>
      </section>
    </>
  );
};
